import { Box, Image } from 'theme-ui'

import Breadcrumb from 'src/components/common/breadcrumb/Breadcrumb'
import Type from 'src/components/gel/Type'
import FormattedText from 'src/components/common/formattedText/FormattedText'

import type { BreadcrumbItem } from 'src/components/common/breadcrumb/Breadcrumb'
import type { BackgroundColorTypes } from 'src/theme/adSales/colors'

type PageHeaderType = 'Ad Formats' | 'Standard'

interface PageHeaderProps {
  breadcrumbs?: BreadcrumbItem[]
  title: string
  subTitle?: string[]
  description?: string
  image?: { src: string; alt: string }
  backgroundColor?: BackgroundColorTypes
}

const SubTitleBlock = ({ subTitle, description }: { subTitle: string[]; description?: string }) => (
  <Box sx={{ mb: description ? [5, null, 7] : 0 }}>
    <Type variant="Labels" as="h2" sx={{ display: 'flex', flexWrap: 'wrap', maxHeight: '56px' }}>
      {subTitle &&
        subTitle.map((line, index) => (
          <>
            {index > 0 && index <= subTitle.length - 1 && (
              <Box as="span" sx={{ mx: 2, mt: '2px', maxHeight: '24px' }}>
                •
              </Box>
            )}
            <Box key={index} as="span" sx={{ mt: 1 }}>
              {line}
            </Box>
          </>
        ))}
    </Type>
  </Box>
)

const AdFormatsHeader = ({
  breadcrumbs,
  title,
  subTitle,
  description,
  image,
  backgroundColor
}: PageHeaderProps) => {
  return (
    <Box sx={{ pb: [6, null, 6, 11], height: '100%', maxHeight: '750px' }}>
      <Box sx={{ display: ['block', null, 'block'], pb: [2, null, 2, 9] }}>
        <Breadcrumb crumbs={breadcrumbs} backgroundColor={backgroundColor} />
      </Box>
      {image ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            height: [null, null, 'auto', '100%']
          }}
        >
          <Box
            sx={{
              width: ['100%', null, '100%', 'calc(38.54% - 19px)'],
              order: [2, null, 2, 1]
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                height: [null, null, 'auto', 'calc(100% - 72px)']
              }}
            >
              <Box
                sx={{
                  pt: [5, null, 5, 0],
                  pb: [5, null, 5, 0],
                  pr: [2, null, 2, 8],
                  pl: [2, null, 2, 4]
                }}
              >
                <Type variant="HeadlineSerif" sx={{ mb: [2, null, 6] }}>
                  {title}
                </Type>
                {subTitle && <SubTitleBlock subTitle={subTitle} description={description} />}
                {description && (
                  <FormattedText text={description} typeStyles={{ WebkitLineClamp: 10 }} />
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: ['100%', null, '100%', 'calc(61.57% + 16px)'],
              px: [2, null, 2, 0],
              order: [1, null, 1, 2],
              position: 'relative',
              height: ['auto', null, 'auto', '100%']
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: ['auto', null, 'auto', '100%'],
                maxHeight: '452px',
                paddingBottom: ['56.25%', null, '56.25%', '0'],
                overflow: 'hidden'
              }}
            >
              <Image
                src={image.src}
                alt={image.alt}
                sx={{
                  position: ['absolute', null, 'absolute', 'static'],
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 'copy', px: [2, null, 2, 4] }}>
          {title && (
            <Type variant="HeadlineSerif" sx={{ mb: 6 }}>
              {title}
            </Type>
          )}
          {subTitle && <SubTitleBlock subTitle={subTitle} description={description} />}
          {description && <FormattedText text={description} truncateLength={550} />}
        </Box>
      )}
    </Box>
  )
}

const StandardHeader = ({
  breadcrumbs,
  title,
  subTitle,
  description,
  image,
  backgroundColor
}: PageHeaderProps) => {
  return (
    <Box sx={{ pb: 0 }}>
      {image ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            height: [null, null, 'auto', '453px']
          }}
        >
          <Box
            sx={{
              width: ['100%', null, '100%', 'calc(38.54% - 19px)'],
              order: [1, null, 1, 1]
            }}
          >
            <Box sx={{ display: ['block', null, 'block'] }}>
              <Breadcrumb crumbs={breadcrumbs} backgroundColor={backgroundColor} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: [null, null, 'auto', 'calc(100% - 72px)']
              }}
            >
              <Box
                sx={{
                  pt: [5, null, 5, 0],
                  pb: [5, null, 5, 0],
                  pr: [2, null, 2, 8],
                  pl: [2, null, 2, 4]
                }}
              >
                <Type variant="HeadlineSerif" sx={{ mb: [6, 7, 6, 5] }}>
                  {title}
                </Type>
                {subTitle && <SubTitleBlock subTitle={subTitle} description={description} />}
                {description && <FormattedText text={description} />}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: ['100%', null, '100%', 'calc(61.57% + 16px)'],
              px: [2, null, 2, 0],
              order: [1, null, 1, 2],
              position: 'relative',
              height: ['auto', null, 'auto', '100%']
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: ['auto', null, 'auto', '100%'],
                paddingBottom: ['56.25%', null, '56.25%', 0],
                overflow: 'hidden'
              }}
            >
              <Image
                src={image.src}
                alt={image.alt}
                sx={{
                  position: ['absolute', null, 'absolute', 'static'],
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 'copy', px: [2, null, 2, 4] }}>
          {title && (
            <Type variant="HeadlineSerif" sx={{ mb: 6 }}>
              {title}
            </Type>
          )}
          {subTitle && <SubTitleBlock subTitle={subTitle} description={description} />}
          {description && <FormattedText text={description} />}
        </Box>
      )}
    </Box>
  )
}

const PageHeader = (props: PageHeaderProps & { type: PageHeaderType }) => {
  return (
    <>
      {props.type === 'Ad Formats' ? <AdFormatsHeader {...props} /> : <StandardHeader {...props} />}
    </>
  )
}

export default PageHeader
